if (!customElements.get('language-selector-modal')) {
  class LanguageSelectorModal extends HTMLElement {
    constructor() {
      super();
      this.storageKey = 'language_popup_seen';
      this.mode = this.dataset.mode || 'first_visit';
      this.closeOnOverlay = this.dataset.closeOnOverlay === 'true';

      this.overlay = this.querySelector('[data-lang-overlay]');
      this.panel = this.querySelector('[data-lang-panel]');
      this.closeButton = this.querySelector('[data-lang-close]');
      this.options = Array.from(this.querySelectorAll('[data-lang-option]'));
      this.localeInput = this.querySelector('[data-lang-input]');
      this.form = this.querySelector('form');

      this.previousActiveElement = null;
      this.onKeydown = this.onKeydown.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onOptionClick = this.onOptionClick.bind(this);
    }

    connectedCallback() {
      this.options.forEach((option) => option.addEventListener('click', this.onOptionClick));

      if (this.closeButton) {
        this.closeButton.addEventListener('click', () => this.close());
      }

      if (this.closeOnOverlay && this.overlay) {
        this.overlay.addEventListener('click', this.onOverlayClick);
      }

      if (this.mode === 'browser_detect') {
        this.preselectFromBrowserLanguage();
      }

      this.evaluateDisplay();
    }

    evaluateDisplay() {
      if (this.mode === 'always') {
        this.open();
        return;
      }

      if (this.mode === 'browser_detect' || this.mode === 'first_visit') {
        if (!this.hasSeenPopup()) {
          this.open();
        }
      }
    }

    hasSeenPopup() {
      try {
        return window.localStorage.getItem(this.storageKey) === 'true';
      } catch (error) {
        return document.cookie.indexOf(`${this.storageKey}=true`) !== -1;
      }
    }

    markPopupSeen() {
      try {
        window.localStorage.setItem(this.storageKey, 'true');
      } catch (error) {
        document.cookie = `${this.storageKey}=true; path=/; max-age=31536000`;
      }
    }

    preselectFromBrowserLanguage() {
      const browserLanguage = (navigator.language || navigator.userLanguage || '').toLowerCase();
      if (!browserLanguage) return;

      const prefix = browserLanguage.slice(0, 2);
      const match = this.options.find((option) => option.dataset.value === prefix);

      if (match) {
        this.options.forEach((option) => option.removeAttribute('data-preselected'));
        match.setAttribute('data-preselected', 'true');
      }
    }

    onOptionClick(event) {
      const option = event.currentTarget;
      const value = option.dataset.value;

      // Mark as seen before navigating away so the popup doesn't reappear
      // once Shopify's localization form finishes its native submission.
      this.markPopupSeen();

      if (this.localeInput) {
        this.localeInput.value = value;
      }

      // Intentionally no preventDefault / custom redirect here beyond
      // setting the hidden field: the button's native type="submit"
      // triggers Shopify's own {% form 'localization' %} submission.
    }

    onOverlayClick() {
      this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Tab') {
        this.trapFocus(event);
      }
    }

    trapFocus(event) {
      const focusable = this.panel.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open() {
      this.previousActiveElement = document.activeElement;
      this.hidden = false;
      requestAnimationFrame(() => {
        this.classList.add('is-open');
      });

      document.addEventListener('keydown', this.onKeydown);
      document.body.style.overflow = 'hidden';

      const firstOption = this.options[0];
      if (firstOption) {
        firstOption.focus();
      } else if (this.panel) {
        this.panel.focus();
      }
    }

    close() {
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);
      document.body.style.overflow = '';

      const speed = parseInt(this.style.getPropertyValue('--lang-popup-anim-speed'), 10) || 300;
      window.setTimeout(() => {
        this.hidden = true;
      }, speed);

      if (this.previousActiveElement && typeof this.previousActiveElement.focus === 'function') {
        this.previousActiveElement.focus();
      }
    }
  }

  customElements.define('language-selector-modal', LanguageSelectorModal);
}
